import { ProductId } from '../value-objects/ProductId';

export type ProductAvailability = 'in_stock' | 'out_of_stock' | 'limited_stock' | 'pre_order' | 'discontinued';

export interface AllergenInfo {
  contains: string[];
  mayContains: string[];
}

export interface AttributeDetail {
  value: string | number;
  label: string;
}

export type ProductAttributes = Record<string, AttributeDetail[]>;

export class Product {
  private constructor(
    private readonly _id: ProductId,
    private readonly _name: string,
    private readonly _description: string,
    private readonly _category: string,
    private readonly _images: string[],
    private readonly _slug: string,
    private readonly _ingredients: string[],
    private readonly _attributes: ProductAttributes,
    private readonly _allergenInfo: AllergenInfo,
    private readonly _availability: ProductAvailability,
    private readonly _isVisible: boolean,
    private readonly _createdAt: Date,
    private readonly _updatedAt: Date,
  ) {}

  static create(
    name: string,
    description: string,
    category: string,
    images: string[] = [],
    ingredients: string[] = [],
    attributes: ProductAttributes = {},
    allergenInfo: AllergenInfo = { contains: [], mayContains: [] },
  ): Product {
    if (!name?.trim()) {
      throw new Error('Product name is required');
    }

    if (!description?.trim()) {
      throw new Error('Product description is required');
    }

    if (!category?.trim()) {
      throw new Error('Product category is required');
    }

    const slug = Product.generateSlug(name);
    const now = new Date();

    return new Product(
      ProductId.generateNewProductId(),
      name.trim(),
      description.trim(),
      category.trim(),
      [...images],
      slug,
      [...ingredients],
      { ...attributes },
      { ...allergenInfo },
      'in_stock',
      true,
      now,
      now,
    );
  }

  static restore(
    id: ProductId,
    name: string,
    description: string,
    category: string,
    images: string[],
    slug: string,
    ingredients: string[],
    attributes: ProductAttributes,
    allergenInfo: AllergenInfo,
    availability: ProductAvailability,
    isVisible: boolean,
    createdAt: Date,
    updatedAt: Date,
  ): Product {
    return new Product(
      id,
      name,
      description,
      category,
      images,
      slug,
      ingredients,
      attributes,
      allergenInfo,
      availability,
      isVisible,
      createdAt,
      updatedAt,
    );
  }

  // private static generateId(): string {
  //   const timestamp = Date.now().toString(36);
  //   const random = Math.random().toString(36).substring(2, 8);
  //   return `prod_${timestamp}_${random}`;
  // }

  private static generateSlug(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  getId(): ProductId {
    return this._id;
  }

  getName(): string {
    return this._name;
  }

  getDescription(): string {
    return this._description;
  }

  getCategory(): string {
    return this._category;
  }

  getImages(): string[] {
    return [...this._images];
  }

  getSlug(): string {
    return this._slug;
  }

  getIngredients(): string[] {
    return [...this._ingredients];
  }

  getAttributes(): ProductAttributes {
    return { ...this._attributes };
  }

  getAllergenInfo(): AllergenInfo {
    return { ...this._allergenInfo };
  }

  getAvailability(): ProductAvailability {
    return this._availability;
  }

  isVisible(): boolean {
    return this._isVisible;
  }

  getCreatedAt(): Date {
    return this._createdAt;
  }

  getUpdatedAt(): Date {
    return this._updatedAt;
  }

  isAvailable(): boolean {
    return this._availability === 'in_stock' || this._availability === 'limited_stock';
  }

  hasAllergen(allergen: string): boolean {
    return this._allergenInfo.contains.includes(allergen) || this._allergenInfo.mayContains.includes(allergen);
  }

  getAttributeOptions(attributeKey: string): AttributeDetail[] {
    return this._attributes[attributeKey] || [];
  }

  hasAttribute(attributeKey: string): boolean {
    return attributeKey in this._attributes;
  }

  equals(other: Product): boolean {
    return this._id.isEqualTo(other._id);
  }
}
